import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';
import { useFormatMessage } from 'hooks';

import Table from 'components/Table';
import paths from 'pages/Router/paths';
import ConfirmationModal from 'components/ConfirmationModal';
import { fetchCountries, deleteCountry, countriesCleanUp } from 'state/actions/countries';

const Countries = () => {
  const { countriesList, isAdmin, error, loading, deleted } = useSelector(
    (state) => ({
      countriesList: state.countries.data,
      isAdmin: state.auth.userData.isAdmin,
      error: state.countries.error,
      loading: state.countries.loading,
      deleted: state.countries.deleted,
    }),
    shallowEqual
  );

  const [deleteModal, setDeleteModal] = useState({
    countryId: null,
    isOpen: false,
  });

  const dispatch = useDispatch();

  const [search, setSearch] = useState('');

  // Extract all useFormatMessage calls at top level (hooks cannot be called conditionally)
  const nameHeader = useFormatMessage('Countries.name');
  const localeHeader = useFormatMessage('Countries.locale');
  const lastModifiedHeader = useFormatMessage('Countries.lastModified');
  const deleteConfirm = useFormatMessage('Countries.delete');
  const deleteTitle = useFormatMessage('Countries.deleteTitle');
  const deleteBody = useFormatMessage('Countries.deleteBody');
  const cancelMessage = useFormatMessage('Countries.cancel');
  const countriesTitle = useFormatMessage('Countries.countries');
  const newCountryLabel = useFormatMessage('Countries.newCountry');
  const searchLabel = useFormatMessage('Countries.search');
  const searchPlaceholder = useFormatMessage('Countries.searchPlaceholder');

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchCountries());
    }
    return () => {
      dispatch(countriesCleanUp());
    };
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (deleted && !loading) {
      setDeleteModal({ isOpen: false, countryId: null });
    }
  }, [deleted, loading]);

  const filteredCountries = countriesList.filter((country) =>
    country.name?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      Header: nameHeader,
      accessor: 'name',
    },
    {
      Header: localeHeader,
      accessor: 'locale',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.locale?.toUpperCase()}
        </small>
      ),
    },
    {
      Header: lastModifiedHeader,
      accessor: 'createdAt',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.createdAt}
        </small>
      ),
    },
    {
      Header: '',
      id: 'actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          {isAdmin && (
            <div className="buttons is-right">
              <Link
                to={`/country/${row.original.id}`}
                className="button is-small is-primary"
              >
                <span className="icon is-small">
                  <i className="mdi mdi-account-edit" />
                </span>
              </Link>
              <button
                type="button"
                className="button is-small is-danger"
                onClick={() =>
                  setDeleteModal({
                    countryId: row.original.id,
                    isOpen: true,
                  })
                }
              >
                <span className="icon is-small">
                  <i className="mdi mdi-trash-can" />
                </span>
              </button>
            </div>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      {deleteModal.isOpen && (
        <ConfirmationModal
          isActive={deleteModal.isOpen}
          isLoading={loading}
          confirmButtonMessage={deleteConfirm}
          title={deleteTitle}
          body={deleteBody}
          cancelButtonMessage={cancelMessage}
          onConfirmation={() => dispatch(deleteCountry(deleteModal.countryId))}
          onCancel={() =>
            setDeleteModal({ countryId: null, isOpen: false })
          }
        />
      )}
      <section className="hero is-hero-bar">
        <div className="hero-body">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <h1 className="title">
                  {countriesTitle}
                </h1>
              </div>
            </div>
            <div className="level-right">
              <div className="level-item">
                <Link
                  to={paths.ADD_COUNTRY}
                  className="button is-primary"
                >
                  {newCountryLabel}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="section is-main-section">
        {error && (
          <article className="message is-danger">
            <div className="message-body">{error}</div>
          </article>
        )}
        <div className="card has-table has-mobile-sort-spaced">
          <header className="card-header">
            <p className="card-header-title">
              <span className="icon">
                <i className="mdi mdi-earth" />
              </span>
              {searchLabel}
            </p>
            <div className="card-header-icon">
              <input
                className="input"
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </header>
          <div className="b-table">
            {loading ? (
              <ClipLoader />
            ) : (
              <Table columns={columns} data={filteredCountries} />
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Countries;
